import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useQuery } from "react-query";
import { getPaymentGateways } from "../../../Services/services";
import { PaymentGateway } from "../../../types";
import { CLOSE } from "../../../assets";
import { Loader2 } from "lucide-react";
import { Store } from "../../../states/store";

interface PaymentGatewayModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (id: number | string) => void;
  /** Pass true while the API call triggered by Continue is in-flight */
  isSubmitting?: boolean;
}

export default function PaymentGatewayModal({
  isOpen,
  onClose,
  onSelect,
  isSubmitting = false,
}: PaymentGatewayModalProps) {
  const { data: gateways, isLoading } = useQuery(
    "payment-gateways",
    getPaymentGateways,
    {
      enabled: !!Store.getState().auths.token,
    },
  );

  const [gateWayId, setGateWayId] = useState<number | null>(null);

  const switchGatewayIcon = (name: string) => {
    switch (name) {
      case "Moniepoint":
        return "https://upload.wikimedia.org/wikipedia/commons/9/9e/Moniepointlogo.png";
      case "Paystack":
        return "https://upload.wikimedia.org/wikipedia/commons/0/0b/Paystack_Logo.png";
      case "Oasis - interswitch":
        return "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3e/Interswitch_logo.svg/960px-Interswitch_logo.svg.png";
      default:
        break;
    }
  };

  useEffect(() => {
    if (gateways && gateways?.length === 1) {
      setGateWayId(gateways[0].id);
    }
  }, [gateways]);
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[1000]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="transform transition duration-300 ease-out"
              enterFrom="opacity-0 scale-95 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="transform transition duration-200 ease-in"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all border border-gray-100">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <Dialog.Title
                      as="h3"
                      className="text-xl font-bold leading-6 text-gray-900"
                    >
                      Select Payment Method
                    </Dialog.Title>
                    <p className="mt-2 text-sm text-gray-500">
                      Choose your preferred payment gateway to proceed.
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="rounded-full p-2 hover:bg-gray-100 transition-colors -mt-4 -mr-2"
                  >
                    <img src={CLOSE} alt="Close" className="w-6 h-6" />
                  </button>
                </div>

                {isLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-10 h-10 animate-spin text-globalGreen" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {gateways?.map((gateway: PaymentGateway) => (
                      <button
                        key={gateway.id}
                        onClick={() => setGateWayId(gateway.id)}
                        className="relative flex flex-col items-center justify-center p-6 border-2 border-gray-100 rounded-2xl hover:border-globalGreen hover:bg-green-50/30 transition-all duration-200 group gap-4 min-h-[140px]"
                      >
                        {/* {gateway.logo ? (
                          <div className="h-12 flex items-center justify-center">
                            <img
                              src={switchGatewayIcon(gateway.name)}
                              alt={gateway.name}
                              className="max-h-full max-w-full object-contain group-hover:scale-110 transition-transform duration-200"
                            />
                          </div>
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 group-hover:bg-green-100 group-hover:text-globalGreen transition-colors">
                            <span className="text-xl font-bold">
                              {gateway.name.charAt(0)}
                            </span>
                          </div>
                        )} */}
                        <div className="h-12 flex items-center justify-center">
                          <img
                            src={switchGatewayIcon(gateway.name)}
                            alt={gateway.name}
                            className="max-h-full max-w-full object-contain group-hover:scale-110 transition-transform duration-200"
                          />
                        </div>
                        {/* <span className="font-semibold text-gray-700 group-hover:text-globalGreen text-sm uppercase tracking-wide">
                          {gateway.name}
                        </span> */}
                        {gateWayId === gateway.id && (
                          <div className="absolute top-3 right-3 h-4 w-4 rounded-full border-2 border-globalGreen bg-globalGreen transition-all" />
                        )}
                        {/* <div className="absolute top-3 right-3 h-4 w-4 rounded-full border-2 border-gray-300 group-hover:border-globalGreen group-hover:bg-globalGreen transition-all" /> */}
                      </button>
                    ))}

                    {(!gateways || gateways.length === 0) && (
                      <div className="col-span-full text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                        <p className="text-gray-500 font-medium">
                          No payment gateways available at the moment.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-8 flex justify-center">
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect
                        x="3"
                        y="11"
                        width="18"
                        height="11"
                        rx="2"
                        ry="2"
                      ></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                    Secure Payment Processing
                  </p>
                </div>
                {gateWayId && (
                  <button
                    onClick={() => onSelect(gateWayId!)}
                    disabled={isSubmitting}
                    className="w-full mt-2 bg-globalGold hover:bg-globalGold/90 disabled:opacity-70 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      "Continue"
                    )}
                  </button>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
